import React, { useEffect, useState, Suspense, lazy } from 'react'
import classNames from 'classnames'
import { Drawer } from 'components/ui'
import {
    NAV_MODE_THEMED,
    NAV_MODE_TRANSPARENT,
    DIR_RTL,
} from 'constants/theme.constant'
import withHeaderItem from 'utils/hoc/withHeaderItem'
import { NavToggle } from 'components/shared'
import { useNavigate } from 'react-router-dom'
import navigationConfig from 'configs/navigation.config'
import pokemonNavConfig from 'configs/pokemonnav.config'
import useResponsive from 'utils/hooks/useResponsive'
import { useSelector } from 'react-redux'
import { ScrollBar, Input } from 'components/ui'
import { useTranslation } from 'react-i18next'
import {
    HiHome
} from 'react-icons/hi'


async function fetchPokemonData(locale) {
    try {
      const searchParameters = {
        language: locale
      };
      const queryParameters = new URLSearchParams(searchParameters).toString();
      const response = await fetch(`https://labmaus-website-b7a5f5f01a05.herokuapp.com/api/recent_top_usage?${queryParameters}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data; // Assuming the API response is an array of Pokemon objects
    } catch (error) {
      console.error('Error fetching Pokemon data:', error);
      return [];
    }
}

const VerticalMenuContent = lazy(() =>
    import('components/template/VerticalMenuContent')
)

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value, debounce, onChange])

    return (
        <div style={{display: "flex", padding: "1.0rem"}}>
            <div className="flex justify-end">
                <div className="flex items-center">
                    <Input
                        {...props}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                </div>
            </div>
        </div>
    )
}

const MobileNavToggle = withHeaderItem(NavToggle)

const MobileNav = () => {
    const [isOpen, setIsOpen] = useState(false)
    const locale = useSelector((state) => state.locale.currentLang)
    const navigate = useNavigate()

    const openDrawer = () => {
        setIsOpen(true)
    }

    const onDrawerClose = (e) => {
        setIsOpen(false)
    }

    const handleRedirectToHome = () => {
        navigate('/home');
        onDrawerClose();
    };

    const [searchTerm, setSearchTerm] = useState('');
    const themeColor = useSelector((state) => state.theme.themeColor)
    const primaryColorLevel = useSelector(
        (state) => state.theme.primaryColorLevel
    )
    const { t } = useTranslation();
    const navMode = useSelector((state) => state.theme.navMode)
    const mode = useSelector((state) => state.theme.mode)
    const direction = useSelector((state) => state.theme.direction)
    const currentRouteKey = useSelector(
        (state) => state.base.common.currentRouteKey
    )
    const sideNavCollapse = useSelector(
        (state) => state.theme.layout.sideNavCollapse
    )
    const userAuthority = useSelector((state) => state.auth.user.authority)

    const { smaller } = useResponsive()

    const navColor = () => {
        if (navMode === NAV_MODE_THEMED) {
            return `bg-${themeColor}-${primaryColorLevel} side-nav-${navMode}`
        }

        if (navMode === NAV_MODE_TRANSPARENT) {
            return `side-nav-${mode}`
        }

        return `side-nav-${navMode}`
    }

    const [usageData, setUsageData] = useState([]);
    const [loading, setLoading] = useState(true); // Add a loading state

    useEffect(() => {
    async function fetchAndUpdateConfig() {
        const pokemonData = await fetchPokemonData(locale)
            setUsageData(pokemonData);
            setLoading(false);
        }
        fetchAndUpdateConfig();
    }, [locale]);

    if (loading) {
        return <div>Loading...</div>
    } else {
        const navTree = navigationConfig
        const generalContent = (
            <VerticalMenuContent
                navMode={navMode}
                collapsed={sideNavCollapse}
                navigationTree={navTree}
                routeKey={currentRouteKey}
                userAuthority={userAuthority}
                onMenuItemClick={onDrawerClose}
                direction={direction}
            />
        )

        const pokemonTree = pokemonNavConfig({pokemonData: usageData, searchTerm: searchTerm})
        const pokemonContent = (
            <VerticalMenuContent
                navMode={navMode}
                collapsed={sideNavCollapse}
                navigationTree={pokemonTree}
                routeKey={currentRouteKey}
                userAuthority={userAuthority}
                onMenuItemClick={onDrawerClose}
                direction={direction}
            />
        )

        return (
            <>
                {smaller.md && (
                    <>
                        <div className="text-2xl" onClick={openDrawer}>
                            <MobileNavToggle toggled={isOpen} />
                        </div>
                        <Drawer
                            title={(
                                <div 
                                    onClick={handleRedirectToHome}
                                    style={{ 
                                        cursor: 'pointer', 
                                        display: 'flex', 
                                        alignItems: 'center' 
                                    }}
                                >
                                    <HiHome style={{ marginRight: '8px', fontSize: '25px' }} />
                                    <h5 style={{ margin: 0, fontSize: '1.5rem' }}>{t('general.home')}</h5>
                                </div>
                            )}
                            isOpen={isOpen}
                            onClose={onDrawerClose}
                            onRequestClose={onDrawerClose}
                            bodyClass={classNames(navColor(), 'p-0')}
                            width={330}
                            placement={direction === DIR_RTL ? 'right' : 'left'}
                        >
                            <Suspense fallback={<></>}>
                                {isOpen && (
                                    <div className="side-nav-content">
                                        <ScrollBar autoHide direction={direction}>
                                        {generalContent}
                                        <div>
                                            <DebouncedInput
                                                value={searchTerm ?? ''}
                                                onChange={(value) => setSearchTerm(String(value))}
                                                className="p-2 font-lg shadow border border-block"
                                                placeholder={t('nav.search')}
                                            />
                                        </div>
                                        {pokemonContent}
                                    </ScrollBar>
                                </div>
                                )}
                            </Suspense>
                        </Drawer>
                    </>
                )}
            </>
        )
    }
}

export default MobileNav
